import React from 'react';
import { Album } from '../../types/album';
import { theme } from '../../styles/theme';

interface AlbumCoverProps {
  album: Album;
  onClick: () => void;
  matchingTracks?: string[];
}

const AlbumCover: React.FC<AlbumCoverProps> = ({ album, onClick, matchingTracks = [] }) => {
  const [imageUrl, setImageUrl] = React.useState<string>('');

  // Helper function to format multiple artists for display
  const formatArtistsShort = (artists: string[]): string => {
    if (!artists || artists.length === 0) return 'Unknown Artist';
    if (artists.length === 1) return artists[0];
    if (artists.length === 2) return `${artists[0]} & ${artists[1]}`;
    
    // For 3+ artists, show first artist + "& others" to save space
    return `${artists[0]} & ${artists.length - 1} others`;
  };

  React.useEffect(() => {
    // Convert blob to object URL for display, fallback to direct URL
    if (album.coverImageBlob && album.coverImageBlob.size > 0) {
      const url = URL.createObjectURL(album.coverImageBlob);
      setImageUrl(url);
      
      return () => URL.revokeObjectURL(url);
    } else if (album.coverImageUrl) {
      // Use direct URL as fallback
      setImageUrl(album.coverImageUrl);
    } else {
      setImageUrl('');
    }
  }, [album.coverImageBlob, album.coverImageUrl]);

  const getFormatIcon = (format?: { type: string; packaging: string; discCount: number }) => {
    if (!format) return null;
    
    // Priority: Box Set > Multiple discs > Special packaging > Type
    if (format.type === 'Box Set') return '📦';
    if (format.discCount > 1) return '💿×' + format.discCount;
    if (format.packaging === 'Gatefold') return '📖';
    if (format.packaging === 'Tri-fold') return '📖📖';
    if (format.type === 'EP') return 'EP';
    if (format.type === 'Single') return '7″';
    
    return null;
  };

  const containerStyle: React.CSSProperties = {
    cursor: 'pointer',
    textAlign: 'center',
    transition: `transform ${theme.transitions.normal}`,
    position: 'relative',
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.md,
    backgroundColor: theme.colors.surface,
  };

  const titleStyle: React.CSSProperties = {
    marginTop: theme.spacing.sm,
    fontSize: '0.875rem',
    fontWeight: '600',
    color: theme.colors.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const artistStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: theme.colors.textSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const formatIcon = getFormatIcon(album.format);

  return (
    <div style={containerStyle} onClick={onClick} className="album-cover">
      <div style={{ position: 'relative' }}>
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={`${formatArtistsShort(album.artists || [album.artist])} - ${album.title}`}
            style={imageStyle}
          />
        ) : (
          <div style={{...imageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <span style={{color: theme.colors.textMuted}}>No Image</span>
          </div>
        )}
        {formatIcon && (
          <div style={{
            position: 'absolute',
            top: theme.spacing.xs,
            right: theme.spacing.xs,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            borderRadius: theme.borderRadius.sm,
            fontSize: '0.75rem',
            fontWeight: '600',
            lineHeight: 1,
            zIndex: 1
          }}>
            {formatIcon}
          </div>
        )}
        {matchingTracks.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: theme.borderRadius.md,
            zIndex: 2,
            padding: theme.spacing.sm
          }}>
            <div style={{
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: '600',
              marginBottom: theme.spacing.xs,
              textAlign: 'center'
            }}>
              🎵 Found Song{matchingTracks.length > 1 ? 's' : ''}:
            </div>
            <div style={{
              color: 'white',
              fontSize: '0.7rem',
              textAlign: 'center',
              lineHeight: 1.2,
              maxHeight: '60px',
              overflow: 'hidden'
            }}>
              {matchingTracks.slice(0, 3).map((track, index) => (
                <div key={index} style={{ 
                  marginBottom: index < Math.min(matchingTracks.length, 3) - 1 ? '2px' : 0,
                  fontWeight: '500'
                }}>
                  {track}
                </div>
              ))}
              {matchingTracks.length > 3 && (
                <div style={{ fontSize: '0.65rem', opacity: 0.8, marginTop: '2px' }}>
                  +{matchingTracks.length - 3} more
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div style={titleStyle} title={album.title}>
        {album.title}
      </div>
      <div style={artistStyle} title={(album.artists || [album.artist]).join(', ')}>
        {formatArtistsShort(album.artists || [album.artist])}
      </div>
    </div>
  );
};

export default AlbumCover;